import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaShip } from "react-icons/fa";
import useFetchVesselById from "../lib/useFetchVesselById";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./vesselDetails.css";

const VesselDetails = () => {
  const { vesselId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSpecSheet, setSelectedSpecSheet] = useState("");
  
  const { vessel, vesselError, vesselLoading } = useFetchVesselById(vesselId);

  if (vesselLoading) {
    return (
      <div className="vessel-details-container">
        <div className="vessel-details-content">
          <Skeleton height={40} width={300} style={{ marginBottom: 20 }} />
          <Skeleton height={30} width={200} style={{ marginBottom: 40 }} />
          <Skeleton height={200} count={3} style={{ marginBottom: 20 }} />
        </div>
      </div>
    );
  }

  if (vesselError) {
    return (
      <div className="vessel-details-container">
        <div className="vessel-details-content">
          <div className="error-message">Error loading vessel details: {vesselError.message}</div>
        </div>
      </div>
    );
  }

  if (!vessel) {
    return (
      <div className="vessel-details-container">
        <div className="vessel-details-content">
          <div className="error-message">Vessel not found</div>
        </div>
      </div>
    );
  }

  const primary = vessel.primaryInformation || {};
  const info = vessel.vesselInformation || {};
  const images = vessel.images || [];

  // Modal functions
  const openModal = (specSheetUrl) => {
    setSelectedSpecSheet(specSheetUrl);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSpecSheet("");
  };

  return (
    <div className="vessel-details-container">
      {/* Breadcrumbs */}
      <div className="breadcrumbs">
        <span onClick={() => navigate("/charterer-dashboard/vessels")} className="breadcrumb-link">
          Chartering
        </span>
        <span className="breadcrumb-separator"> &gt; </span>
        <span className="breadcrumb-link">{primary.vesselType || "Vessel"}</span>
        <span className="breadcrumb-separator"> &gt; </span>
        <span className="breadcrumb-current">{primary.vesselName || "Vessel Details"}</span>
      </div>

      {/* Navigation Tabs */}
      <div className="vessel-tabs">
        <button
          className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === "specifications" ? "active" : ""}`}
          onClick={() => setActiveTab("specifications")}
        >
          Specifications
        </button>
        <button
          className={`tab-button ${activeTab === "gallery" ? "active" : ""}`}
          onClick={() => setActiveTab("gallery")}
        >
          Gallery
        </button>
      </div>

      {/* Main Content */}
      <div className="vessel-main-content">
        {/* Vessel Title and Pricing */}
        <div className="vessel-header">
          <div className="vessel-title-section">
            <h1 className="vessel-title">{primary.vesselName || "Vessel"}</h1>
            <div className="vessel-price">${primary.pricing?.toLocaleString() || "0"}/Day</div>
          </div>
          <button 
            className="request-charter-btn" 
            onClick={() => navigate(`/charterer-dashboard/vessels/${vesselId}/request-charter`)}
          >
            <FaShip style={{ marginRight: "8px" }} />
            Request a charter
          </button>
        </div>

        {activeTab === "overview" && (
          <div className="overview-content">
            {/* Key Specifications */}
            <div className="key-specs">
              <div className="spec-item">
                <div className="spec-label">Deck space</div>
                <div className="spec-value">{info.deckSpace || "??"} m²</div>
              </div>
              <div className="spec-item">
                <div className="spec-label">Pax accommodation</div>
                <div className="spec-value">{info.numberOfPassengers || "??"}</div>
              </div>
              <div className="spec-item">
                <div className="spec-label">Deck strength</div>
                <div className="spec-value">{info.deckStrength || "??"}T/m²</div>
              </div>
              <div className="spec-item">
                <div className="spec-label">BHP</div>
                <div className="spec-value">{info.bhp || "??"}</div>
              </div>
              <div className="spec-item">
                <div className="spec-label">Bollard pull</div>
                <div className="spec-value">{info.bollardPull || "??"}T</div>
              </div>
            </div>

            {/* Certifications */}
            <div className="certifications">
              <div className="cert-item">
                <span className="cert-label">sgs certified:</span>
                <span className="cert-value">Yes</span>
              </div>
              <div className="cert-item">
                <span className="cert-label">Classification:</span>
                <span className="cert-value">{info.classification || "??"}</span>
              </div>
            </div>

            {/* Description */}
            <div className="vessel-description">
              <p>{primary.vesselDescription || "No description available."}</p>
            </div>

            {/* Vessel Particulars */}
            <div className="vessel-particulars">
              <h3>Vessel particulars</h3>
              <div className="particulars-grid">
                <div className="particulars-column">
                  <div className="particular-item">
                    <span className="particular-label">Port of registry:</span>
                    <span className="particular-value">{info.portOfRegistry || "??"}</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Flag:</span>
                    <span className="particular-value">??</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Call sign:</span>
                    <span className="particular-value">{info.callSign || "??"}</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Official number:</span>
                    <span className="particular-value">{info.officialNumber || "??"}</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">IMO number:</span>
                    <span className="particular-value">{info.imoNumber || "??"}</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">MMSI number:</span>
                    <span className="particular-value">{info.mmsiNumber || "??"}</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Bollard pull:</span>
                    <span className="particular-value">{info.bollardPull || "??"}T</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Length overall:</span>
                    <span className="particular-value">{info.lengthOverall || "??"}m</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Beam moulded:</span>
                    <span className="particular-value">{info.beamMoulded || "??"}m</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Draft:</span>
                    <span className="particular-value">{info.draft || "??"}m</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Gross Tonnage:</span>
                    <span className="particular-value">{info.grossTonnage || "??"}</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Net Tonnage:</span>
                    <span className="particular-value">??</span>
                  </div>
                </div>
                <div className="particulars-column">
                  <div className="particular-item">
                    <span className="particular-label">Main engine:</span>
                    <span className="particular-value">??</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Cruising speed:</span>
                    <span className="particular-value">{info.cruisingSpeed || "??"} knots</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Range:</span>
                    <span className="particular-value">{info.range || "??"} nm</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Fuel capacity:</span>
                    <span className="particular-value">{info.fuelCapacity || "??"} m³</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Propulsion:</span>
                    <span className="particular-value">{info.propulsion || "??"} kW</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Generators:</span>
                    <span className="particular-value">{info.generators || "??"}</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Water capacity:</span>
                    <span className="particular-value">{info.waterCapacity || "??"} m³</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Class:</span>
                    <span className="particular-value">{info.classification || "??"}</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Builder:</span>
                    <span className="particular-value">{info.builder || "??"}</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Keel Laid:</span>
                    <span className="particular-value">{info.keelLaid || "??"}</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Build year:</span>
                    <span className="particular-value">{info.yearOfBuilt || "??"}</span>
                  </div>
                  <div className="particular-item">
                    <span className="particular-label">Owners:</span>
                    <span className="particular-value">??</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              {vessel.documents?.specSheet && (
                <button
                  className="view-spec-btn"
                  onClick={() => openModal(vessel.documents.specSheet)}
                >
                  View spec sheet
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === "specifications" && (
          <div className="specifications-content">
            <h3>Detailed Specifications</h3>
            <p>Detailed specifications content will go here...</p>
          </div>
        )}

        {activeTab === "gallery" && (
          <div className="gallery-content">
            <h3>Gallery</h3>
            <div className="image-gallery">
              {images.length > 0 ? (
                <div className="gallery-grid">
                  {images.map((image, index) => (
                    <div key={index} className="gallery-item">
                      <img src={image} alt={`Vessel ${index + 1}`} />
                    </div>
                  ))}
                </div>
              ) : (
                <p>No images available</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Specification Sheet Modal */}
      {isModalOpen && (
        <div className="spec-sheet-modal-overlay" onClick={closeModal}>
          <div className="spec-sheet-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="spec-sheet-modal-header">
              <h3>Specification Sheet</h3>
              <button onClick={closeModal} className="spec-sheet-modal-close">×</button>
            </div>
            <div className="spec-sheet-modal-body">
              {selectedSpecSheet && (
                <iframe
                  src={`https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(selectedSpecSheet)}`}
                  title="Specification Sheet"
                  width="100%"
                  height="600px"
                  style={{ border: 'none' }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VesselDetails; 